import { SiteFooter } from '@/components/site/site-footer';
import { SiteHeader } from '@/components/site/site-header';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Link, Stack } from 'expo-router';
import React, { useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, Platform, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SEO_EASE } from './seo-blocks';

/** A partir de quanto rolado o CTA fixo do celular aparece (o hero já saiu). */
const DOCK_AFTER = 520;

interface SeoPageShellProps {
  /** Título da aba/pilha de navegação. */
  screenTitle: string;
  /** `<SeoHead />` da página: fica fora do ScrollView, como na home. */
  head: React.ReactNode;
  /** Frase curta ao lado do botão no CTA fixo do celular. */
  dockLabel: string;
  children: React.ReactNode;
}

/**
 * Casca de toda página pública de SEO (/para, /guias, /comparar,
 * /profissionais): cabeçalho e rodapé da home, fundo claro e o CTA fixo no
 * rodapé do celular, que sobe depois que o hero sai da tela.
 */
export function SeoPageShell({ screenTitle, head, dockLabel, children }: SeoPageShellProps) {
  const insets = useSafeAreaInsets();
  const [showDock, setShowDock] = useState(false);

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const next = e.nativeEvent.contentOffset.y > DOCK_AFTER;
    if (next !== showDock) setShowDock(next);
  };

  return (
    <View className="flex-1 bg-background">
      <Stack.Screen options={{ title: screenTitle, headerShown: false }} />
      {head}
      <SiteHeader />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1"
        onScroll={handleScroll}
        scrollEventThrottle={32}
      >
        <View role="main">{children}</View>
        <SiteFooter />
        <View className="h-20 md:hidden" />
      </ScrollView>

      <View
        pointerEvents={showDock ? 'auto' : 'none'}
        className="absolute bottom-0 left-0 right-0 z-[90] flex-row items-center gap-3 border-t border-neutral-900/5 bg-card px-4 pt-3 shadow-2xl md:hidden"
        style={{
          paddingBottom: (Platform.OS !== 'web' ? insets.bottom : 0) + 12,
          transform: [{ translateY: showDock ? 0 : 120 }],
          ...(Platform.OS === 'web'
            ? ({
                transitionProperty: 'transform',
                transitionDuration: '700ms',
                transitionTimingFunction: SEO_EASE,
              } as object)
            : null),
        }}
      >
        <ThemedText className="flex-1 font-semibold text-sm" numberOfLines={2}>
          {dockLabel}
        </ThemedText>
        <Link href="/(auth)/register" asChild>
          <Button size="sm" title="Começar grátis" />
        </Link>
      </View>
    </View>
  );
}
